import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sparkles, Check, Loader2, ArrowRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { ticketService } from '../../services/api';

export const AiMatchModal = ({ isOpen, onClose, project, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [stats, setStats] = useState(null);
  const [applying, setApplying] = useState(null);

  const analyze = async () => {
    setLoading(true);
    setSuggestions(null);
    try {
      const res = await api.post('/tickets/ai-match-suggestions', { project });
      setSuggestions(res.data.suggestions || []);
      setStats({ ventes: res.data.ventes_analysees, achats: res.data.achats_disponibles });
      if ((res.data.suggestions || []).length === 0) {
        toast.info(res.data.message || "L'IA n'a trouvé aucun appariement fiable.");
      }
    } catch (e) {
      toast.error(`Erreur IA: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applySuggestion = async (sugg, idx) => {
    setApplying(idx);
    try {
      await ticketService.manualAttribution(
        sugg.vente.id,
        sugg.achats.map(a => a.id),
        false,
        project
      );
      toast.success(`Attribution appliquée : ${sugg.vente.evenement?.slice(0, 40)}`);
      setSuggestions(prev => prev.filter((_, i) => i !== idx));
      onSuccess?.();
    } catch (e) {
      toast.error(`Échec: ${e.response?.data?.detail || e.message}`);
    } finally {
      setApplying(null);
    }
  };

  const confidenceColor = (c) =>
    c >= 90 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
    : c >= 70 ? 'bg-amber-500/20 text-amber-400 border-amber-500/40'
    : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/40';

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-zinc-950 border-[#C4A052]/30" data-testid="ai-match-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#C4A052]">
            <Sparkles className="h-5 w-5" /> Attribution intelligente (IA)
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            L'IA analyse les VENTES non attribuées et les ACHATS disponibles du projet <span className="text-white font-semibold">{project}</span>, puis propose des appariements. Rien n'est appliqué sans ton accord.
          </DialogDescription>
        </DialogHeader>

        {!suggestions && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Button
              onClick={analyze}
              disabled={loading}
              data-testid="ai-match-analyze-button"
              className="bg-[#C4A052] hover:bg-[#D4B86A] text-black font-semibold px-6"
            >
              {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse en cours...</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Analyser avec l'IA</>)}
            </Button>
            {loading && <p className="text-xs text-zinc-500">L'IA compare les événements, catégories et quantités...</p>}
          </div>
        )}

        {suggestions && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-zinc-400">
              <span data-testid="ai-match-stats">{stats?.ventes} ventes analysées · {stats?.achats} achats disponibles · {suggestions.length} suggestion(s)</span>
              <Button variant="ghost" size="sm" onClick={analyze} disabled={loading} className="text-[#C4A052]">
                {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Ré-analyser'}
              </Button>
            </div>

            {suggestions.length === 0 && (
              <p className="text-center text-zinc-500 py-6">Aucun appariement fiable trouvé.</p>
            )}

            {suggestions.map((s, idx) => (
              <div key={s.vente.id} data-testid={`ai-suggestion-${idx}`} className="border border-zinc-800 rounded-lg p-3 bg-zinc-900/50 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Badge className={`border ${confidenceColor(s.confidence)}`}>{s.confidence}% sûr</Badge>
                  <div className="flex items-center gap-2">
                    {s.complete
                      ? <Badge className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">Complet {s.total_qty}/{s.vente_qty}</Badge>
                      : <Badge className="bg-sky-500/20 text-sky-400 border border-sky-500/40">Partiel {s.total_qty}/{s.vente_qty}</Badge>}
                    <Button
                      size="sm"
                      onClick={() => applySuggestion(s, idx)}
                      disabled={applying !== null}
                      data-testid={`ai-apply-${idx}`}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white h-7"
                    >
                      {applying === idx ? <Loader2 className="h-3 w-3 animate-spin" /> : (<><Check className="h-3 w-3 mr-1" /> Appliquer</>)}
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                  <div className="bg-red-500/5 border border-red-500/20 rounded p-2">
                    <p className="text-[10px] uppercase text-red-400 font-semibold mb-1">VENTE ({s.vente_qty} billet{s.vente_qty > 1 ? 's' : ''})</p>
                    <p className="text-white truncate">{s.vente.evenement}</p>
                    <p className="text-zinc-400 text-xs">{s.vente.categorie} · {s.vente.date_evenement}{s.vente.nom_client ? ` · ${s.vente.nom_client}` : ''}</p>
                  </div>
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-2">
                    <p className="text-[10px] uppercase text-emerald-400 font-semibold mb-1">ACHAT{s.achats.length > 1 ? 'S' : ''} proposé{s.achats.length > 1 ? 's' : ''}</p>
                    {s.achats.map(a => (
                      <p key={a.id} className="text-white text-xs truncate flex items-center gap-1">
                        <ArrowRight className="h-3 w-3 text-emerald-400 flex-shrink-0" />
                        {a.evenement} · {a.categorie} · x{a.qty}{a.source ? ` · ${a.source}` : ''}
                      </p>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-zinc-500 italic">💡 {s.reason}</p>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="ai-match-close-button" className="text-zinc-400">
            <X className="h-4 w-4 mr-1" /> Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
